import { useQuery } from "@tanstack/react-query"
import { Search } from "lucide-react"
import { useRef, useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"

type Concepto = {
  codigo: string
  actividad: string
  pnr: string | null
  pnnr: string | null
  pjd: string | null
  pjnd: string | null
  nota: string | null
}

type Catalogo = { valor_ut: string; conceptos: Concepto[] }

const pct = (v: string | null) => (v === null ? "—" : `${Number(v).toLocaleString("es-VE")} %`)

/** Sin tildes ni mayúsculas: «honorarios» encuentra «Honorarios». */
const plano = (t: string) =>
  t
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()

/**
 * El catálogo de retenciones de ISLR del Decreto 1.808 (art. 9, Gaceta 36.203), para
 * consultarlo (22/10). Es el mismo que propone la alícuota en la orden de pago
 * (`services/islr_1808.py`): se lee del servidor, no se copia aquí.
 */
export function CatalogoIslr() {
  const [buscar, setBuscar] = useState("")
  const vista = useRef<HTMLElement | null>(null)
  const catalogo = useQuery({
    queryKey: ["islr-conceptos", null],
    queryFn: () => api.get<Catalogo>("/api/v1/islr/conceptos"),
  })

  if (catalogo.isPending) return <EstadoCarga />
  if (catalogo.isError)
    return (
      <EstadoError
        titulo="No se pudo cargar el catálogo"
        error={catalogo.error}
        onReintentar={() => catalogo.refetch()}
      />
    )

  const q = plano(buscar.trim())
  const filas = catalogo.data.conceptos.filter(
    (c) => q === "" || plano(`${c.codigo} ${c.actividad} ${c.nota ?? ""}`).includes(q),
  )

  return (
    <section ref={vista} className="space-y-4">
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Porcentajes de retención de ISLR del Decreto 1.808, artículo 9 (Gaceta Oficial 36.203,
        12/05/1997), sobre el monto pagado o abonado en cuenta. PNR: persona natural residente ·
        PNNR: no residente · PJD: persona jurídica domiciliada · PJND: no domiciliada. «—»: no
        aplica o tarifa N° 2.
      </p>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <label className="relative block w-full max-w-md">
          <Search
            size={16}
            aria-hidden={true}
            className="absolute top-1/2 left-3 -translate-y-1/2"
            style={{ color: "var(--fg-muted)" }}
          />
          <input
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Honorarios, flete, arrendamiento…"
            aria-label="Buscar en el catálogo"
            className="campo w-full pl-9"
          />
        </label>
        <Exportar
          titulo="Catálogo de retenciones de ISLR"
          subtitulo={[
            "Decreto 1.808, art. 9 · PNR, PNNR, PJD y PJND",
            `UT ${catalogo.data.valor_ut} Bs`,
            buscar.trim() && `Búsqueda: «${buscar.trim()}»`,
          ]
            .filter(Boolean)
            .join(" · ")}
          contenido={vista}
        />
      </div>

      <Tabla etiqueta="Catálogo de retenciones de ISLR, Decreto 1.808">
        <Cabecera>
          <Th>Numeral</Th>
          <Th>Actividad</Th>
          <Th derecha>PNR</Th>
          <Th derecha>PNNR</Th>
          <Th derecha>PJD</Th>
          <Th derecha>PJND</Th>
        </Cabecera>
        <Cuerpo>
          {filas.length === 0 && <TablaVacia colSpan={6}>Nada coincide con «{buscar}».</TablaVacia>}
          {filas.map((c) => (
            <Fila key={c.codigo}>
              <Td numero>{c.codigo}</Td>
              <Td>
                {c.actividad}
                {c.nota && (
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {c.nota}
                  </span>
                )}
              </Td>
              <Td derecha numero>
                {pct(c.pnr)}
              </Td>
              <Td derecha numero>
                {pct(c.pnnr)}
              </Td>
              <Td derecha numero>
                {pct(c.pjd)}
              </Td>
              <Td derecha numero>
                {pct(c.pjnd)}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>

      <div className="tarjeta space-y-1 p-4 text-sm">
        <p className="font-medium">Sustraendo (Parágrafo Segundo)</p>
        <p style={{ color: "var(--fg-muted)" }}>
          Sólo a la persona natural residente: UT × porcentaje × 83,3334. Con la UT de{" "}
          <span className="tabular-nums">{catalogo.data.valor_ut}</span> Bs (unidad tributaria
          vigente en la configuración fiscal). En la orden de pago, elegir el concepto propone la
          alícuota y el sustraendo según el RIF del proveedor.
        </p>
      </div>
    </section>
  )
}
